/**
 * SandboxAssessment — exact replica of the internal Zero Trust Reference
 * Architecture page, using the SAME visual components.
 *
 * Reuses directly:
 * - PillarGrid → PillarColumn → CapabilityCard → MaturityBar (pillar visualization)
 * - AssessmentPanel (slide-in assessment with maturity selector, vendor suggestions)
 * - MaturitySelector (rich maturity picker with descriptions)
 * - CapabilitySuggestion (vendor/product recommendation cards)
 *
 * Data adapter: shapes sessionStorage data to match internal component props.
 * Vendor suggestions come from useSandboxSuggestions (published config / hardcoded fallback).
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Shield, ClipboardCheck, BarChart2, AlertTriangle,
  Search, Download, Eye,
  Key, Monitor, Globe, AppWindow, Database,
  User, Wifi, Lightbulb, CheckCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSandboxSession } from './hooks/useSandboxSession';
import { useSandboxSuggestions } from './hooks/useSandboxSuggestions';
import { generateAnalysis } from './services/sandboxAnalysis';
import SandboxBanner from './SandboxBanner';
import ConversionModal from './ConversionModal';

// Reuse internal ZT visual components
import PillarGrid from '../components/ZeroTrust/layers/PillarGrid';
import AssessmentPanel from '../components/ZeroTrust/assessment/AssessmentPanel';
import MaturityBar from '../components/ZeroTrust/components/MaturityBar';

import {
  PILLAR_DEFINITIONS,
  MATURITY_KEYS,
  MATURITY_COLORS,
  SANDBOX_CONFIG_KEY,
} from '../config/zeroTrustConstants';

/* ----------------------------------------------------------------
   Lucide icons for pillars
   ---------------------------------------------------------------- */
const PILLAR_ICONS = {
  IDENTITY: Key,
  DEVICE: Monitor,
  NETWORK: Globe,
  APPLICATION: AppWindow,
  DATA: Database,
  VISIBILITY: Eye,
};

const SIGNAL_INPUTS = [
  { key: 'IDENTITY', label: 'Identity Signal', icon: User, color: 'var(--zt-identity, #6366f1)' },
  { key: 'DEVICE', label: 'Device Signal', icon: Monitor, color: 'var(--zt-device, #8b5cf6)' },
  { key: 'NETWORK', label: 'Risk Signal', icon: Wifi, color: 'var(--zt-network, #06b6d4)' },
];

const GOVERNANCE_ITEMS = [
  { name: 'AB 869 Compliance', critical: true },
  { name: 'Zero Trust Steering Committee' },
  { name: 'Data Governance Council' },
  { name: 'Architecture Review Board' },
  { name: 'Identity-First Access Policy' },
  { name: 'Least Privilege Enforcement' },
  { name: 'Data Classification Policy' },
  { name: 'Continuous Verification Policy' },
  { name: 'CISA ZT Maturity Model' },
];

/* ----------------------------------------------------------------
   Data helpers
   ---------------------------------------------------------------- */

function getPillars() {
  try {
    const raw = localStorage.getItem(SANDBOX_CONFIG_KEY);
    if (raw) {
      const config = JSON.parse(raw);
      if (config?.pillars?.length > 0) {
        return config.pillars.map(p => ({
          pillar_id: p.code,
          pillar_code: p.code,
          pillar_name: p.name,
          color: p.color,
          capabilities: (p.capabilities || []).map(c => ({
            zt_capability_id: c.id || c.code,
            capability_code: c.code,
            capability_name: c.name,
            capability_description: c.description || '',
            ab869_critical: c.isCritical || false,
          })),
        }));
      }
    }
  } catch { /* fall through */ }
  return getDefaultPillars();
}

function getDefaultPillars() {
  const defaults = {
    IDENTITY: [
      { code: 'idp', name: 'IdP', desc: 'Centralized identity management and single sign-on.', critical: false },
      { code: 'mfa', name: 'MFA \u2013 Phishing-Resistant FIDO2', desc: 'Phishing-resistant MFA across all users.', critical: true },
      { code: 'pam', name: 'Privileged Access Management', desc: 'Just-in-time privileged access with session recording.', critical: true },
      { code: 'idgov', name: 'Identity Governance & Admin (IGA)', desc: 'Automated provisioning, access reviews, SoD enforcement.', critical: false },
      { code: 'rbac', name: 'Role-Based Access Control', desc: 'Least-privilege access model with defined roles.', critical: false },
    ],
    DEVICE: [
      { code: 'edr', name: 'EDR / XDR', desc: 'Behavioral analysis and threat detection on all endpoints.', critical: true },
      { code: 'mdm', name: 'UEM / MDM', desc: 'Device enrollment with encryption and remote wipe.', critical: false },
      { code: 'compliance', name: 'Device Posture Assessment', desc: 'Health checks enforced before granting access.', critical: false },
      { code: 'patching', name: 'Patch Management', desc: 'Automated OS and third-party patching.', critical: false },
      { code: 'inventory', name: 'Certificate-Based Device Identity', desc: 'Cryptographic device identity for zero-trust access.', critical: false },
    ],
    NETWORK: [
      { code: 'ztna', name: 'ZTNA \u2013 Replace VPN', desc: 'Identity-aware access replacing traditional VPN.', critical: false },
      { code: 'microseg', name: 'Microsegmentation', desc: 'Least-privilege network policies between workloads.', critical: true },
      { code: 'dns', name: 'DNS Security', desc: 'Protective DNS filtering and monitoring.', critical: false },
      { code: 'encrypt', name: 'Encrypted Communications', desc: 'All traffic encrypted via mTLS or tunnels.', critical: false },
      { code: 'sdwan', name: 'SD-WAN / SASE', desc: 'Software-defined WAN with integrated security.', critical: false },
    ],
    APPLICATION: [
      { code: 'access', name: 'App Access Proxy', desc: 'Identity-aware access per application.', critical: false },
      { code: 'api', name: 'API Gateway & Security', desc: 'API gateway with auth, rate limiting, schema validation.', critical: false },
      { code: 'workload', name: 'Workload / Service Identity', desc: 'Service mesh with mTLS between microservices.', critical: true },
      { code: 'container', name: 'Service Mesh', desc: 'Runtime security for containerized workloads.', critical: false },
      { code: 'waf', name: 'WAF / Bot Protection', desc: 'OWASP Top 10 protection with custom rules.', critical: false },
    ],
    DATA: [
      { code: 'classify', name: 'Data Classification', desc: 'Automated discovery and labeling across repositories.', critical: false },
      { code: 'dlp', name: 'DLP', desc: 'Prevent sensitive data exfiltration at endpoint, network, cloud.', critical: true },
      { code: 'encrest', name: 'Encryption (at-rest + in-transit + in-use)', desc: 'Customer-managed keys with rotation policies.', critical: false },
      { code: 'governance', name: 'Data Access Governance', desc: 'Enforce data access policies based on classification.', critical: false },
      { code: 'rights', name: 'Data Rights Management', desc: 'Persistent protection that travels with files.', critical: false },
    ],
    VISIBILITY: [
      { code: 'siem', name: 'SIEM / Log Aggregation', desc: 'Centralized security event collection.', critical: true },
      { code: 'soar', name: 'SOAR / Automated Response', desc: 'Automated incident response playbooks.', critical: false },
      { code: 'threatintel', name: 'Threat Intelligence', desc: 'Integrated feeds correlated with internal telemetry.', critical: false },
      { code: 'ir', name: 'Incident Response Plan', desc: 'Documented and tested IR plan.', critical: false },
      { code: 'metrics', name: 'Security Metrics & Reporting', desc: 'Executive KPI dashboards.', critical: false },
    ],
  };
  return PILLAR_DEFINITIONS.map(p => ({
    pillar_id: p.code,
    pillar_code: p.code,
    pillar_name: p.name,
    color: p.cssVar,
    capabilities: (defaults[p.code] || []).map(c => ({
      zt_capability_id: c.code,
      capability_code: c.code,
      capability_name: c.name,
      capability_description: c.desc,
      ab869_critical: c.critical || false,
    })),
  }));
}

/* ----------------------------------------------------------------
   Component
   ---------------------------------------------------------------- */

export default function SandboxAssessment() {
  const navigate = useNavigate();
  const session = useSandboxSession();
  const {
    scenarioLabel, orgType, techStack, complianceNeeds, riskProfile,
    assessments, answers, analysisResult, analysisComplete,
    updateAssessment, setAnalysisResult
  } = session;

  const [viewMode, setViewMode] = useState('architecture');
  const [assessmentPanelOpen, setAssessmentPanelOpen] = useState(false);
  const [showConversion, setShowConversion] = useState(false);

  const PILLARS = useMemo(() => getPillars(), []);
  const mainPillars = useMemo(() => PILLARS.filter(p => p.pillar_code !== 'VISIBILITY'), [PILLARS]);
  const visibilityPillar = useMemo(() => PILLARS.find(p => p.pillar_code === 'VISIBILITY'), [PILLARS]);

  // Framework object shaped for AssessmentPanel
  const sandboxFramework = useMemo(() => ({ pillars: PILLARS }), [PILLARS]);

  // Sandbox suggestions (same interface as useSuggestions)
  const { getSuggestionForCapability, fetchTransactions } = useSandboxSuggestions();

  // ── Map sandbox assessments to internal component format ──

  const mappedAssessments = useMemo(() => {
    return assessments.map(a => ({
      zt_capability_id: a.capId || a.capKey?.split(':')?.[1],
      maturity_level: a.maturity,
      implementation_status: a.status,
      priority: a.priority,
      current_solution: a.currentSolution || '',
    }));
  }, [assessments]);

  // Handler for internal components (CapabilityCard, AssessmentPanel)
  const handleUpdateAssessment = useCallback(async (capabilityId, updates) => {
    const pillar = PILLARS.find(p =>
      p.capabilities.some(c => c.zt_capability_id === capabilityId)
    );
    if (!pillar) return;

    const key = `${pillar.pillar_code}:${capabilityId}`;
    const existing = assessments.find(a => a.capKey === key);
    const updated = {
      capKey: key,
      pillar: pillar.pillar_code,
      capId: capabilityId,
      maturity: updates.maturity_level || existing?.maturity || 'TRADITIONAL',
      status: updates.implementation_status || existing?.status || 'NOT_STARTED',
      priority: updates.priority || existing?.priority || 'MEDIUM',
      currentSolution: updates.current_solution || existing?.currentSolution || '',
    };
    updateAssessment(pillar.pillar_code, updated);
  }, [PILLARS, assessments, updateAssessment]);

  // ── Analysis ──

  const handleRunAnalysis = useCallback(() => {
    const result = generateAnalysis({
      assessments, answers, techStack, complianceNeeds, orgType, riskProfile,
    });
    setAnalysisResult(result);
    setViewMode('results');
    setShowConversion(true);
  }, [assessments, answers, techStack, complianceNeeds, orgType, riskProfile, setAnalysisResult]);

  // ── Export ──

  const handleExport = () => {
    window.print();
  };

  // ── Stats ──

  const stats = useMemo(() => {
    const totalCaps = PILLARS.reduce((s, p) => s + p.capabilities.length, 0);
    const assessedCaps = assessments.filter(a => a.maturity && a.maturity !== 'TRADITIONAL').length;
    return { totalCaps, assessedCaps };
  }, [assessments, PILLARS]);

  // ── Gap data ──

  const gapData = useMemo(() => {
    const gaps = [];
    PILLARS.forEach(pillar => {
      pillar.capabilities.forEach(cap => {
        const a = mappedAssessments.find(x => x.zt_capability_id === cap.zt_capability_id);
        const maturityIdx = MATURITY_KEYS.indexOf(a?.maturity_level || 'TRADITIONAL');
        if (maturityIdx < 2) {
          gaps.push({
            pillar: pillar.pillar_code,
            pillarName: pillar.pillar_name,
            capId: cap.zt_capability_id,
            capability: cap.capability_name,
            description: cap.capability_description,
            isCritical: cap.ab869_critical,
            currentMaturity: a?.maturity_level || 'TRADITIONAL',
            targetMaturity: 'ADVANCED',
            priority: cap.ab869_critical ? 'CRITICAL' : (maturityIdx === 0 ? 'HIGH' : 'MEDIUM'),
          });
        }
      });
    });
    return gaps;
  }, [PILLARS, mappedAssessments]);

  // Summary for AssessmentPanel
  const sandboxSummary = useMemo(() => {
    if (!analysisResult) return null;
    return {
      overallScore: analysisResult.summary.overallScore * 25,
      readinessLevel: analysisResult.summary.readinessLevel,
      pillarScores: analysisResult.summary.pillarScores.map(ps => ({
        pillar_code: ps.pillar,
        score: ps.score * 25,
      })),
    };
  }, [analysisResult]);

  // ── Render ──

  return (
    <div className="sb-assess">
      <SandboxBanner />

      <div className="zt-container sb-zt-container">
        {/* Header */}
        <div className="zt-header">
          <div className="zt-header-left">
            <div className="zt-header-title">
              <Shield size={22} style={{ color: 'var(--color-primary, #6366f1)' }} />
              <div>
                <h1>Zero Trust Reference Architecture</h1>
                <p className="sb-zt-subtitle">
                  CISA Pillar-Aligned &middot; AB 869 Compliant &middot;
                  {' '}{stats.assessedCaps}/{stats.totalCaps} capabilities assessed
                </p>
              </div>
            </div>

            <div className="zt-view-tabs">
              <button
                className={`zt-view-tab ${viewMode === 'architecture' ? 'active' : ''}`}
                onClick={() => setViewMode('architecture')}
              >
                <Shield size={14} />
                Architecture
              </button>
              <button
                className={`zt-view-tab ${viewMode === 'architecture' && assessmentPanelOpen ? 'active' : ''}`}
                onClick={() => { setViewMode('architecture'); setAssessmentPanelOpen(true); }}
              >
                <ClipboardCheck size={14} />
                Assessment
              </button>
              <button
                className={`zt-view-tab ${viewMode === 'results' ? 'active' : ''}`}
                onClick={() => analysisComplete ? setViewMode('results') : null}
                disabled={!analysisComplete}
              >
                <BarChart2 size={14} />
                Executive
              </button>
            </div>
          </div>

          <div className="zt-header-right">
            {analysisComplete && (
              <button className="btn-secondary" onClick={handleExport}>
                <Download size={14} />
                Export PDF
              </button>
            )}
            <button className="sb-assess-run-btn sb-assess-run-btn--header" onClick={handleRunAnalysis}>
              <BarChart2 size={14} />
              Run analysis
            </button>
          </div>
        </div>

        {/* ═══ ARCHITECTURE VIEW ═══ */}
        {viewMode === 'architecture' && (
          <div className="zt-scroll-area">
            {/* Governance Layer */}
            <div className="governance-layer">
              <div className="layer-header">
                <div className="layer-header-left">
                  <Shield size={16} style={{ color: 'var(--zt-governance, #10b981)' }} />
                  <span className="layer-title">Governance & Policy</span>
                  <span className="layer-subtitle">Cross-cutting capabilities spanning all pillars</span>
                </div>
              </div>
              <div className="governance-items">
                {GOVERNANCE_ITEMS.map((item, i) => (
                  <div key={i} className={`governance-item ${item.critical ? 'governance-item--critical' : ''}`}>
                    <div className="governance-item-header">
                      <span className="governance-item-name">{item.name}</span>
                      {item.critical && (
                        <span className="governance-critical-tag">
                          <AlertTriangle size={10} /> AB 869
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Policy Decision Point */}
            <div className="pdp-layer">
              <div className="layer-header">
                <div className="layer-header-left">
                  <Shield size={16} style={{ color: 'var(--color-primary, #6366f1)' }} />
                  <span className="layer-title">Policy Decision Point (PDP)</span>
                  <span className="layer-subtitle">Continuous verification engine — never trust, always verify</span>
                </div>
              </div>
              <div className="pdp-content">
                <div className="pdp-signals">
                  {SIGNAL_INPUTS.map(signal => {
                    const Icon = signal.icon;
                    return (
                      <div key={signal.key} className="pdp-signal-card">
                        <div className="pdp-signal-icon" style={{ backgroundColor: `${signal.color}22`, color: signal.color }}>
                          <Icon size={16} />
                        </div>
                        <span className="pdp-signal-label">{signal.label}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="pdp-engine">
                  <div className="pdp-engine-inner">
                    <Shield size={28} className="pdp-engine-icon" />
                    <div className="pdp-engine-title">Policy Engine</div>
                    <div className="pdp-engine-principles">
                      <span className="pdp-principle">Verify Explicitly</span>
                      <span className="pdp-principle">Least Privilege</span>
                      <span className="pdp-principle">Assume Breach</span>
                    </div>
                  </div>
                </div>
                <div className="pdp-decisions">
                  <div className="pdp-decision pdp-decision--allow">
                    <span className="pdp-decision-label">Allow</span>
                    <div className="pdp-decision-desc">Verified & Compliant</div>
                  </div>
                  <div className="pdp-decision pdp-decision--conditional">
                    <span className="pdp-decision-label">Conditional</span>
                    <div className="pdp-decision-desc">Step-up Auth Required</div>
                  </div>
                  <div className="pdp-decision pdp-decision--deny">
                    <span className="pdp-decision-label">Deny</span>
                    <div className="pdp-decision-desc">Non-compliant / High Risk</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pillar Grid — SAME component as internal ZT */}
            {mainPillars.length > 0 && (
              <PillarGrid
                pillars={mainPillars}
                assessments={mappedAssessments}
                vendorOverlay={null}
                assessmentMode={true}
                onUpdateAssessment={handleUpdateAssessment}
              />
            )}

            {/* Visibility & Analytics Row */}
            {visibilityPillar && (
              <div className="visibility-detail-row">
                <div className="layer-header">
                  <div className="layer-header-left">
                    <Eye size={16} style={{ color: 'var(--zt-visibility, #ec4899)' }} />
                    <span className="layer-title" style={{ color: 'var(--zt-visibility, #ec4899)' }}>
                      Visibility & Analytics
                    </span>
                    <span className="layer-subtitle">Cross-pillar monitoring, SIEM, and telemetry</span>
                  </div>
                </div>
                <div className="visibility-capabilities">
                  {visibilityPillar.capabilities.map(cap => {
                    const assessment = mappedAssessments.find(a => a.zt_capability_id === cap.zt_capability_id);
                    return (
                      <div
                        key={cap.zt_capability_id}
                        className={`visibility-cap-card ${cap.ab869_critical ? 'visibility-cap-card--critical' : ''}`}
                        style={{ borderLeftColor: 'var(--zt-visibility, #ec4899)' }}
                      >
                        <div className="visibility-cap-header">
                          <Eye size={12} style={{ color: 'var(--zt-visibility, #ec4899)', flexShrink: 0 }} />
                          <span className="visibility-cap-name">{cap.capability_name}</span>
                          {cap.ab869_critical && (
                            <span className="capability-ab869-badge">
                              <AlertTriangle size={10} /> AB 869
                            </span>
                          )}
                        </div>
                        <div className="capability-card-maturity" style={{ marginTop: 6 }}>
                          <MaturityBar
                            currentMaturity={assessment?.maturity_level || null}
                            onSelect={(level) => handleUpdateAssessment(cap.zt_capability_id, { maturity_level: level })}
                            interactive={true}
                            compact
                          />
                        </div>
                        {assessment?.current_solution && (
                          <div className="visibility-cap-solution">{assessment.current_solution}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ RESULTS / EXECUTIVE VIEW ═══ */}
        {viewMode === 'results' && analysisResult && (
          <div className="zt-scroll-area">
            <div className="sb-watermark" aria-hidden="true">SANDBOX</div>

            <div className="sb-exec-stats">
              <div className="sb-exec-stat-card">
                <Shield size={24} style={{ color: MATURITY_COLORS[analysisResult.summary.readinessLevel] }} />
                <div className="sb-exec-stat-info">
                  <span className="sb-exec-stat-label">Overall Readiness</span>
                  <span className="sb-exec-stat-value" style={{ color: MATURITY_COLORS[analysisResult.summary.readinessLevel] }}>
                    {analysisResult.summary.readinessLevel}
                  </span>
                </div>
                <span className="sb-exec-score">{analysisResult.summary.overallScore.toFixed(1)} / 4.0</span>
              </div>
              <div className="sb-exec-stat-card">
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <div className="sb-exec-stat-info">
                  <span className="sb-exec-stat-label">Total Gaps</span>
                  <span className="sb-exec-stat-value">{analysisResult.gaps.totalGaps}</span>
                </div>
              </div>
              <div className="sb-exec-stat-card">
                <ClipboardCheck size={24} style={{ color: '#6366f1' }} />
                <div className="sb-exec-stat-info">
                  <span className="sb-exec-stat-label">Assessed</span>
                  <span className="sb-exec-stat-value">{stats.assessedCaps} / {stats.totalCaps}</span>
                </div>
              </div>
            </div>

            <div className="sb-results-section">
              <h3>Pillar Maturity</h3>
              <div className="sb-results-pillars">
                {analysisResult.summary.pillarScores.map(ps => {
                  const PIcon = PILLAR_ICONS[ps.pillar] || Shield;
                  return (
                    <div key={ps.pillar} className="sb-results-pillar">
                      <div className="sb-results-pillar-label">
                        <PIcon size={14} style={{ color: MATURITY_COLORS[ps.maturity] }} />
                        <span className="sb-results-pillar-name">{ps.pillarName}</span>
                      </div>
                      <div className="sb-results-pillar-bar">
                        <div className="sb-results-pillar-fill" style={{ width: `${(ps.score / 4) * 100}%`, background: MATURITY_COLORS[ps.maturity] }} />
                      </div>
                      <span className="sb-results-pillar-maturity" style={{ color: MATURITY_COLORS[ps.maturity] }}>
                        {ps.maturity}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="sb-results-section">
              <h3>Gap Summary</h3>
              {analysisResult.gaps.gapsByPillar
                .filter(gp => gp.count > 0)
                .map(gp => (
                  <div key={gp.pillar} className="sb-gap-pillar">
                    <h4>{gp.pillarName} ({gp.count} gaps)</h4>
                    {gp.gaps.map((g, i) => (
                      <div key={i} className="sb-gap-item">
                        <span className={`sb-gap-priority sb-gap-priority--${g.priority.toLowerCase()}`}>
                          {g.priority}
                        </span>
                        <div>
                          <strong>{g.capability}</strong>
                          <p>{g.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
            </div>

            {analysisResult.summary.recommendations.length > 0 && (
              <div className="sb-results-section">
                <h3>Recommendations</h3>
                <div className="sb-results-recs">
                  {analysisResult.summary.recommendations.map((rec, i) => (
                    <div key={i} className="sb-rec-item">
                      <div className="sb-rec-header">
                        <span className={`sb-rec-priority sb-rec-priority--${rec.priority.toLowerCase()}`}>{rec.priority}</span>
                        <span className="sb-rec-pillar">{rec.pillarName}</span>
                        <span className="sb-rec-effort">{rec.estimatedEffort}</span>
                      </div>
                      <p>{rec.recommendation}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <button className="btn-secondary" onClick={handleExport} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 24px', fontSize: '0.9rem' }}>
                <Download size={16} />
                Export as PDF
              </button>
            </div>
          </div>
        )}

        {/* Assessment Panel — SAME component as internal ZT */}
        <AssessmentPanel
          isOpen={assessmentPanelOpen}
          onClose={() => setAssessmentPanelOpen(false)}
          department={{ department_id: 'sandbox', common_name: scenarioLabel || 'Sandbox' }}
          framework={sandboxFramework}
          assessments={mappedAssessments}
          gaps={gapData}
          summary={sandboxSummary}
          loading={false}
          saving={false}
          onUpdateAssessment={handleUpdateAssessment}
          onReload={() => {}}
        />
      </div>

      {showConversion && <ConversionModal onClose={() => setShowConversion(false)} />}
    </div>
  );
}
